import { Business } from '../types';

// OpenStreetMap API configuration
const OSM_API_BASE = 'https://api.openstreetmap.org/api/0.6';
const OSM_DEV_API_BASE = 'https://master.apis.dev.openstreetmap.org/api/0.6'; // For testing

interface OSMCredentials {
  username: string;
  password: string;
}

interface OSMChangeset {
  id: number;
  open: boolean;
}

interface OSMNode {
  id?: number;
  version?: number;
  lat: number;
  lon: number;
  tags: Record<string, string>;
}

// Store credentials securely (in production, use proper OAuth)
let osmCredentials: OSMCredentials | null = null;
let currentChangeset: OSMChangeset | null = null;

export const setOSMCredentials = (username: string, password: string) => {
  osmCredentials = { username, password };
};

export const clearOSMCredentials = () => {
  osmCredentials = null;
  currentChangeset = null;
};

export const isAuthenticated = (): boolean => {
  return osmCredentials !== null;
};

// Create HTTP Basic Auth header
const getAuthHeader = (): string => {
  if (!osmCredentials) {
    throw new Error('OSM credentials not set');
  }
  const credentials = btoa(`${osmCredentials.username}:${osmCredentials.password}`);
  return `Basic ${credentials}`;
};

// Create a new changeset
export const createChangeset = async (comment: string): Promise<number> => {
  if (!osmCredentials) {
    throw new Error('Must be authenticated to create changeset');
  }

  const changesetXML = `<?xml version="1.0" encoding="UTF-8"?>
<osm version="0.6" generator="XMR Business Directory">
  <changeset>
    <tag k="created_by" v="XMR Business Directory"/>
    <tag k="comment" v="${comment}"/>
    <tag k="source" v="User submission"/>
    <tag k="type" v="business_addition"/>
  </changeset>
</osm>`;

  try {
    const response = await fetch(`${OSM_API_BASE}/changeset/create`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'text/xml',
        'Authorization': getAuthHeader(),
      },
      body: changesetXML,
    });

    if (!response.ok) {
      throw new Error(`Failed to create changeset: ${response.status} ${response.statusText}`);
    }

    const changesetId = parseInt(await response.text());
    currentChangeset = { id: changesetId, open: true };
    
    console.log(`Created OSM changeset: ${changesetId}`);
    return changesetId;
  } catch (error) {
    console.error('Error creating changeset:', error);
    throw error;
  }
};

// Close a changeset
export const closeChangeset = async (changesetId: number): Promise<void> => {
  if (!osmCredentials) {
    throw new Error('Must be authenticated to close changeset');
  }

  try {
    const response = await fetch(`${OSM_API_BASE}/changeset/${changesetId}/close`, {
      method: 'PUT',
      headers: {
        'Authorization': getAuthHeader(),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to close changeset: ${response.status} ${response.statusText}`);
    }

    if (currentChangeset?.id === changesetId) {
      currentChangeset = null;
    }
    
    console.log(`Closed OSM changeset: ${changesetId}`);
  } catch (error) {
    console.error('Error closing changeset:', error);
    throw error;
  }
};

// Convert business to OSM node format
const businessToOSMNode = (business: Omit<Business, 'id'>): OSMNode => {
  const tags: Record<string, string> = {
    name: business.name,
    amenity: getOSMAmenity(business.category),
  };

  // Add XMR payment tags
  tags['payment:xmr'] = 'yes';
  tags['currency:XMR'] = 'yes';
  
  if (business.xmrPaymentMethods.length > 0) {
    tags['payment:cryptocurrency'] = business.xmrPaymentMethods.join(';');
  }

  // Add contact information
  if (business.website) tags.website = business.website;
  if (business.phone) tags.phone = business.phone;
  if (business.email) tags.email = business.email;
  if (business.openHours) tags.opening_hours = business.openHours;
  if (business.description) tags.description = business.description;

  // Add address components if available
  if (business.address) {
    const addressParts = parseAddress(business.address);
    Object.assign(tags, addressParts);
  }

  return {
    lat: business.coordinates.lat,
    lon: business.coordinates.lng,
    tags,
  };
};

// Map business categories to OSM amenity tags
const getOSMAmenity = (category: string): string => {
  const mapping: Record<string, string> = {
    restaurant: 'restaurant',
    tech: 'office',
    retail: 'shop',
    service: 'office',
    hotel: 'hotel',
    entertainment: 'entertainment',
  };
  return mapping[category] || 'office';
};

// Parse address into OSM address tags
const parseAddress = (address: string): Record<string, string> => {
  const tags: Record<string, string> = {};
  const parts = address.split(',').map(part => part.trim());
  
  if (parts.length >= 1) tags['addr:full'] = address;
  if (parts.length >= 2) tags['addr:city'] = parts[parts.length - 2];
  if (parts.length >= 1) tags['addr:country'] = parts[parts.length - 1];
  
  return tags;
};

// Add business to OpenStreetMap
export const addBusinessToOSM = async (business: Omit<Business, 'id'>): Promise<string> => {
  if (!osmCredentials) {
    throw new Error('Must be authenticated to add business to OSM');
  }

  try {
    // Create changeset if none exists
    if (!currentChangeset?.open) {
      await createChangeset(`Add XMR business: ${business.name}`);
    }

    const osmNode = businessToOSMNode(business);
    const nodeXML = `<?xml version="1.0" encoding="UTF-8"?>
<osm version="0.6" generator="XMR Business Directory">
  <node changeset="${currentChangeset!.id}" lat="${osmNode.lat}" lon="${osmNode.lon}">
    ${Object.entries(osmNode.tags)
      .map(([key, value]) => `    <tag k="${escapeXML(key)}" v="${escapeXML(value)}"/>`)
      .join('\n')}
  </node>
</osm>`;

    const response = await fetch(`${OSM_API_BASE}/node/create`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'text/xml',
        'Authorization': getAuthHeader(),
      },
      body: nodeXML,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to create OSM node: ${response.status} ${response.statusText}\n${errorText}`);
    }

    const nodeId = await response.text();
    console.log(`Created OSM node: ${nodeId} for business: ${business.name}`);
    
    return nodeId;
  } catch (error) {
    console.error('Error adding business to OSM:', error);
    throw error;
  }
};

// Escape XML special characters
const escapeXML = (str: string): string => {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
};

// Test OSM API connection
export const testOSMConnection = async (): Promise<boolean> => {
  if (!osmCredentials) {
    return false;
  }

  try {
    const response = await fetch(`${OSM_API_BASE}/user/details`, {
      headers: {
        'Authorization': getAuthHeader(),
      },
    });

    return response.ok;
  } catch (error) {
    console.error('OSM connection test failed:', error);
    return false;
  }
};

// Get user details from OSM
export const getOSMUserDetails = async (): Promise<any> => {
  if (!osmCredentials) {
    throw new Error('Must be authenticated');
  }

  try {
    const response = await fetch(`${OSM_API_BASE}/user/details`, {
      headers: {
        'Authorization': getAuthHeader(),
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get user details: ${response.status}`);
    }

    const xmlText = await response.text();
    // Parse XML response (simplified)
    return { xml: xmlText };
  } catch (error) {
    console.error('Error getting user details:', error);
    throw error;
  }
};

// Validate business data before submission
export const validateBusinessForOSM = (business: Omit<Business, 'id'>): string[] => {
  const errors: string[] = [];

  if (!business.name || business.name.trim().length < 2) {
    errors.push('Business name must be at least 2 characters long');
  }

  if (!business.coordinates || !business.coordinates.lat || !business.coordinates.lng) {
    errors.push('Valid coordinates are required');
  }

  if (Math.abs(business.coordinates.lat) > 90) {
    errors.push('Latitude must be between -90 and 90');
  }

  if (Math.abs(business.coordinates.lng) > 180) {
    errors.push('Longitude must be between -180 and 180');
  }

  if (!business.category) {
    errors.push('Business category is required');
  }

  if (!business.xmrPaymentMethods || business.xmrPaymentMethods.length === 0) {
    errors.push('At least one XMR payment method is required');
  }

  return errors;
};

// Batch operations for multiple businesses
export const addMultipleBusinessesToOSM = async (
  businesses: Array<Omit<Business, 'id'>>
): Promise<{ success: string[]; failed: Array<{ business: Omit<Business, 'id'>; error: string }> }> => {
  const results = { success: [], failed: [] };

  // Create a single changeset for all businesses
  const changesetId = await createChangeset(
    `Batch add ${businesses.length} XMR businesses`
  );

  for (const business of businesses) {
    try {
      const validationErrors = validateBusinessForOSM(business);
      if (validationErrors.length > 0) {
        results.failed.push({
          business,
          error: `Validation failed: ${validationErrors.join(', ')}`
        });
        continue;
      }

      const nodeId = await addBusinessToOSM(business);
      results.success.push(nodeId);
    } catch (error) {
      results.failed.push({
        business,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    }
  }

  // Close the changeset
  await closeChangeset(changesetId);

  return results;
};